/* @ts-nocheck */
/* global google */
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  GoogleMap,
  useLoadScript,
} from '@react-google-maps/api';

// Deck.gl imports
import { GoogleMapsOverlay } from '@deck.gl/google-maps';
import { PathLayer, LineLayer, ColumnLayer, IconLayer, TextLayer, ScatterplotLayer } from '@deck.gl/layers';

import { 
  ChevronDownIcon, 
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  MapIcon,
  TableCellsIcon,
} from '@heroicons/react/24/outline';
import { 
  SatelliteIcon, 
  RotateCwSquare, 
  RotateCcwSquare, 
  ArrowUpFromLine, 
  ArrowDownFromLine,
  Plus,
  Minus
} from 'lucide-react';

const LIBRARIES = ['geometry'];

const mapContainerStyle = {
  width: '100%',
  height: '100%',
};

const defaultCenter = {
  lat: 42.0308,
  lng: -93.6319
};

// ProVAL-aligned view modes
const VIEW_MODES = [
  { id: 'fixed-interval', label: 'Fixed Interval', description: 'IRI values by segment with labels' },
  { id: 'wheelpath-profile', label: 'Wheelpath Profile', description: 'IRI columns for each wheelpath' },
  { id: 'short-continuous', label: 'Short Continuous Defects', description: 'Localized Roughness (ALR)' },
  { id: 'rolling-straightedge', label: 'Rolling Straightedge', description: 'Bumps & Dips' },
];

// Header control button component
const ControlButton = ({ children, onClick, isActive = false, disabled = false, title }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    title={title}
    className={`p-2 transition-colors ${
      isActive 
        ? 'bg-red-600 text-white' 
        : 'bg-white text-gray-600 hover:bg-gray-100 active:bg-gray-200'
    } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
  >
    {children}
  </button>
);

// IRI Colors (same as FusionMap)
const DEFAULT_IRI_RANGES = [
  { label: 'Excellent', max: 40, description: '< 40 in/mi' },
  { label: 'Very Good', max: 60, description: '40-60 in/mi' },
  { label: 'Satisfactory', max: 90, description: '60-90 in/mi' },
  { label: 'Poor', max: 140, description: '90-140 in/mi' },
  { label: 'Very Poor', max: 9999, description: '> 140 in/mi' }
];

// ALR magnitude colors (same as FusionMap)
const getALRColor = (magnitude) => {
  if (magnitude > 250) return [244, 67, 54, 200];
  if (magnitude > 200) return [255, 152, 0, 200];
  return [255, 193, 7, 200];
};

// Bump/Dip colors
const getBumpColor = (magnitude) => {
  if (magnitude < 0.5) return [255, 179, 179, 200];
  if (magnitude < 1.0) return [255, 102, 102, 200];
  return [255, 0, 0, 220];
};

const getDipColor = (magnitude) => {
  if (magnitude < 0.5) return [255, 213, 179, 200];
  if (magnitude < 1.0) return [255, 177, 102, 200];
  return [255, 140, 0, 220];
};

// IRI text layer constants
const IRI_TEXT_ZOOM_THRESHOLD = 15;

// Data Table Component
const DataTable = ({ 
  columns, 
  data, 
  selectedId, 
  onRowClick, 
  idKey = 'id',
  formatStation 
}) => {
  const selectedRowRef = useRef(null);
  const containerRef = useRef(null);

  // Auto-scroll to selected row (with delay to allow drawer animation to complete)
  useEffect(() => {
    if (selectedRowRef.current && containerRef.current) {
      // Drawer has 300ms transition, wait for it to finish
      const timer = setTimeout(() => {
        if (!selectedRowRef.current || !containerRef.current) return;
        
        const container = containerRef.current;
        const row = selectedRowRef.current;
        const headerHeight = 32; // Approximate sticky header height
        
        // Calculate position to center the row (accounting for header)
        const rowTop = row.offsetTop;
        const rowHeight = row.offsetHeight;
        const containerHeight = container.clientHeight;
        
        const scrollTo = rowTop - headerHeight - (containerHeight - headerHeight - rowHeight) / 2;
        
        container.scrollTo({
          top: Math.max(0, scrollTo),
          behavior: 'smooth'
        });
      }, 350);
      return () => clearTimeout(timer);
    }
  }, [selectedId]);

  return (
    <div ref={containerRef} className="h-full overflow-auto">
      <table className="w-full text-xs">
        <thead className="bg-gray-50 sticky top-0">
          <tr>
            {columns.map((col) => (
              <th 
                key={col.key} 
                className="px-2 sm:px-3 py-1.5 sm:py-2 text-left font-semibold text-gray-600 whitespace-nowrap border-b border-gray-200 text-xs"
              >
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((row, idx) => {
            const isSelected = row[idKey] === selectedId;
            return (
              <tr
                key={row[idKey] || idx}
                ref={isSelected ? selectedRowRef : null}
                onClick={() => onRowClick(row)}
                className={`cursor-pointer transition-colors ${
                  isSelected 
                    ? 'bg-red-50 border-l-2 border-red-600' 
                    : 'hover:bg-gray-50 border-l-2 border-transparent'
                }`}
              >
                {columns.map((col) => (
                  <td 
                    key={col.key} 
                    className={`px-2 sm:px-3 py-1.5 sm:py-2 whitespace-nowrap text-xs ${
                      col.mono ? 'font-mono' : ''
                    } ${isSelected ? 'text-gray-900' : 'text-gray-700'}`}
                  >
                    {col.render ? col.render(row, formatStation) : row[col.key]}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
      {/* Spacer for mobile browser navigation */}
      <div className="h-20 sm:h-0" />
    </div>
  );
};

// Table column definitions for each view type
const getTableColumns = (viewType) => {
  switch (viewType) {
    case 'segment':
      return [
        { key: 'from_station_ft', label: 'Start', mono: true, render: (row, fmt) => fmt(row.from_station_ft) },
        { key: 'to_station_ft', label: 'End', mono: true, render: (row, fmt) => fmt(row.to_station_ft) },
        { key: 'iri_left', label: 'Left IRI', render: (row) => (row.iri_left_in_per_mi || 0).toFixed(1) },
        { key: 'iri_right', label: 'Right IRI', render: (row) => (row.iri_right_in_per_mi || 0).toFixed(1) },
        { key: 'mri', label: 'MRI', render: (row) => (row.iri_avg_in_per_mi || 0).toFixed(1) },
        { key: 'distance_ft', label: 'Length (ft)', render: (row) => (row.distance_ft || 0).toFixed(1) },
      ];
    case 'alr':
      return [
        { key: 'from_station_ft', label: 'Start', mono: true, render: (row, fmt) => fmt(row.from_station_ft) },
        { key: 'to_station_ft', label: 'End', mono: true, render: (row, fmt) => fmt(row.to_station_ft) },
        { key: 'magnitude_in', label: 'Magnitude', render: (row) => (row.magnitude_in || 0).toFixed(1) },
        { key: 'wheelpath', label: 'Wheelpath', render: (row) => row.wheelpath || '—' },
        { key: 'length', label: 'Length (ft)', render: (row) => ((row.to_station_ft || 0) - (row.from_station_ft || 0)).toFixed(1) },
      ];
    case 'bump':
    case 'dip':
      return [
        { key: 'from_station_ft', label: 'Start', mono: true, render: (row, fmt) => fmt(row.from_station_ft) },
        { key: 'to_station_ft', label: 'End', mono: true, render: (row, fmt) => fmt(row.to_station_ft) },
        { key: 'magnitude_in', label: 'Magnitude (in)', render: (row) => (row.magnitude_in || 0).toFixed(2) },
        { key: 'wheelpath', label: 'Wheelpath', render: (row) => row.wheelpath || '—' },
        { key: 'peak_station_ft', label: 'Peak', mono: true, render: (row, fmt) => fmt(row.peak_station_ft) },
      ];
    default:
      return [];
  }
};

function SharedFusionMap({ 
  sessionId,
  segments = [], 
  events = [],
  bounds,
  session,
  projectConfig,
  iriProfileData = null,
  elevationProfileData = null,
  kmlMarkers = null
}) {
  const mapRef = useRef(null);
  const deckOverlayRef = useRef(null);
  const hasOrientedRef = useRef(false);

  // Core map state
  const [map, setMap] = useState(null);
  const [mapCenter, setMapCenter] = useState(defaultCenter);
  const [mapZoom, setMapZoom] = useState(15);
  const [currentZoom, setCurrentZoom] = useState(15);
  const [mapStyleMode, setMapStyleMode] = useState('satellite');

  // View state - default to fixed-interval
  const [activeView, setActiveView] = useState('fixed-interval');
  const [viewDropdownOpen, setViewDropdownOpen] = useState(false);
  const viewDropdownRef = useRef(null);
  const mobileViewDropdownRef = useRef(null);

  // Wheelpath filter
  const [wheelpathFilter, setWheelpathFilter] = useState({
    LEFT: true,
    RIGHT: true
  });

  // Selection state
  const [selectedItem, setSelectedItem] = useState(null);
  const [hoverTooltip, setHoverTooltip] = useState(null);
  
  // UI state
  const [legendCollapsed, setLegendCollapsed] = useState(true); // Collapsed by default on mobile

  // Derived values
  const isReversed = useMemo(() => session?.meta?.is_reverse_stationing === true, [session]);
  const threshold = projectConfig?.iri_threshold_type_a || 160;

const COLOR_PALETTES = {
  2: ['#2EFF2E', '#D50000'],
  3: ['#2EFF2E', '#FFEB3B', '#D50000'],
  4: ['#2EFF2E', '#00A300', '#FFEB3B', '#D50000'],
  5: ['#2EFF2E', '#00A300', '#FFEB3B', '#FFA500', '#D50000'],
};

const IRI_COLOR_SCALE = useMemo(() => {
  const customRanges = projectConfig?.iri_color_scale;

  const isValidScale = Array.isArray(customRanges) && 
    customRanges.length >= 2 && 
    customRanges.length <= 5;

  const sourceRanges = isValidScale ? customRanges : DEFAULT_IRI_RANGES;
  const colors = COLOR_PALETTES[sourceRanges.length] || COLOR_PALETTES[5];

  const finalRanges = sourceRanges.map((range, index) => ({
    ...range,
    color: colors[index]
  }));

  return {
    ranges: finalRanges,
    exceptionColor: '#00FFFF',
    defaultColor: '#A0A0A0'
  };
}, [projectConfig]);

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY,
    libraries: LIBRARIES,
    version: 'beta',
  });

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      const isOutsideDesktop = viewDropdownRef.current && !viewDropdownRef.current.contains(e.target);
      const isOutsideMobile = mobileViewDropdownRef.current && !mobileViewDropdownRef.current.contains(e.target);
      
      // Close if click is outside both dropdowns
      if (isOutsideDesktop && isOutsideMobile) {
        setViewDropdownOpen(false);
      }
      // If only one ref exists, check that one
      if (!viewDropdownRef.current && isOutsideMobile) {
        setViewDropdownOpen(false);
      }
      if (!mobileViewDropdownRef.current && isOutsideDesktop) {
        setViewDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Parse geometry helper (same as FusionMap)
  const parseGeometry = useCallback((geom) => {
    try {
      if (typeof geom === 'object' && geom?.type === 'LineString' && geom.coordinates) {
        return { type: 'LineString', coordinates: geom.coordinates };
      }
      if (typeof geom === 'string') {
        if (geom.startsWith('LINESTRING')) {
          const coordsStr = geom.replace('LINESTRING(', '').replace(')', '');
          const coords = coordsStr.split(',').map(pair => {
            const [lng, lat] = pair.trim().split(' ').map(parseFloat);
            return [lng, lat];
          }).filter(coord => !isNaN(coord[0]) && !isNaN(coord[1]));
          return { type: 'LineString', coordinates: coords };
        } else if (geom.startsWith('{') || geom.startsWith('[')) {
          const parsed = JSON.parse(geom);
          if (parsed?.type === 'LineString' && parsed.coordinates) {
            return { type: 'LineString', coordinates: parsed.coordinates };
          }
        }
      }
      return null;
    } catch (err) {
      console.error('Error parsing geometry:', err);
      return null;
    }
  }, []);

  // Format station helper
  const formatStation = useCallback((station) => {
    if (station === null || station === undefined) return '—';
    const hundreds = Math.floor(station / 100);
    const remainder = station % 100;
    return `${hundreds}+${remainder.toFixed(1).padStart(4, '0')}`;
  }, []);

  // Get segment color (same as FusionMap)
  const getSegmentColor = useCallback((segment) => {
    if (segment.exception) return IRI_COLOR_SCALE.exceptionColor;
    const iri = segment.iri_avg_in_per_mi || segment.iri_in_per_mi || 0;
    if (iri <= 0) return IRI_COLOR_SCALE.defaultColor;
    for (const range of IRI_COLOR_SCALE.ranges) {
      if (iri <= range.max) return range.color;
    }
    return IRI_COLOR_SCALE.defaultColor;
  }, [IRI_COLOR_SCALE]);

  // Get IRI rating
  const getIRIRating = useCallback((iri) => {
    for (const range of IRI_COLOR_SCALE.ranges) {
      if (iri <= range.max) return range.label;
    }
    return 'Unknown';
  }, [IRI_COLOR_SCALE]);

  // ========================================
  // DERIVED DATA FOR EACH VIEW
  // ========================================

  // Segments for Fixed Interval and Wheelpath Profile
  const visibleSegments = useMemo(() => {
    if (activeView !== 'fixed-interval' && activeView !== 'wheelpath-profile') return [];
    return segments.filter(s => !s.exception).sort((a, b) => a.from_station_ft - b.from_station_ft);
  }, [segments, activeView]);

  // ALRs for Short Continuous Defects
  const visibleALRs = useMemo(() => {
    if (activeView !== 'short-continuous') return [];
    return (events || []).filter(e => {
      const isLR = (e.event_type || '').toUpperCase() === 'LR';
      const wheelpathMatch = 
        (e.wheelpath === 'Left' && wheelpathFilter.LEFT) ||
        (e.wheelpath === 'Right' && wheelpathFilter.RIGHT) ||
        !e.wheelpath;
      return isLR && e.geom && wheelpathMatch;
    }).sort((a, b) => a.from_station_ft - b.from_station_ft);
  }, [events, activeView, wheelpathFilter]);

  // Bumps & Dips for Rolling Straightedge
  const visibleBumps = useMemo(() => {
    if (activeView !== 'rolling-straightedge') return [];
    return (events || []).filter(e => {
      const type = (e.event_type || '').toUpperCase();
      const isBump = type === 'BUMP';
      const wheelpathMatch = 
        (e.wheelpath === 'Left' && wheelpathFilter.LEFT) ||
        (e.wheelpath === 'Right' && wheelpathFilter.RIGHT) ||
        !e.wheelpath;
      return isBump && wheelpathMatch;
    }).sort((a, b) => a.from_station_ft - b.from_station_ft);
  }, [events, activeView, wheelpathFilter]);

  const visibleDips = useMemo(() => {
    if (activeView !== 'rolling-straightedge') return [];
    return (events || []).filter(e => {
      const type = (e.event_type || '').toUpperCase();
      const isDip = type === 'DIP';
      const wheelpathMatch = 
        (e.wheelpath === 'Left' && wheelpathFilter.LEFT) ||
        (e.wheelpath === 'Right' && wheelpathFilter.RIGHT) ||
        !e.wheelpath;
      return isDip && wheelpathMatch;
    }).sort((a, b) => a.from_station_ft - b.from_station_ft);
  }, [events, activeView, wheelpathFilter]);

  // Combined bumps + dips for rendering
  const visibleBumpsDips = useMemo(() => {
    return [...visibleBumps, ...visibleDips].sort((a, b) => a.from_station_ft - b.from_station_ft);
  }, [visibleBumps, visibleDips]);

  // Get table data based on active view and selection type
  const tableData = useMemo(() => {
    if (!selectedItem) return [];
    
    switch (selectedItem.type) {
      case 'segment':
        return visibleSegments;
      case 'alr':
        return visibleALRs;
      case 'bump':
        return visibleBumps;
      case 'dip':
        return visibleDips;
      default:
        return [];
    }
  }, [selectedItem, visibleSegments, visibleALRs, visibleBumps, visibleDips]);

  // Get ID key based on selection type
  const getIdKey = useCallback((type) => {
    switch (type) {
      case 'segment': return 'segment_id';
      case 'alr': 
      case 'bump':
      case 'dip': return 'event_id';
      default: return 'id';
    }
  }, []);

  // Navigation
  const currentItemIndex = useMemo(() => {
    if (!selectedItem || !tableData.length) return -1;
    const idKey = getIdKey(selectedItem.type);
    return tableData.findIndex(item => item[idKey] === selectedItem.data[idKey]);
  }, [selectedItem, tableData, getIdKey]);

  const navigateItem = useCallback((direction) => {
    if (!tableData.length || currentItemIndex === -1) return;
    const newIndex = direction === 'prev' 
      ? Math.max(0, currentItemIndex - 1)
      : Math.min(tableData.length - 1, currentItemIndex + 1);
    
    if (newIndex !== currentItemIndex) {
      setSelectedItem({ type: selectedItem.type, data: tableData[newIndex] });
    }
  }, [tableData, currentItemIndex, selectedItem]);

  const handleRowClick = useCallback((row) => {
    if (!selectedItem) return;
    setSelectedItem({ type: selectedItem.type, data: row });
  }, [selectedItem]);

  // Check if drawer can be opened for current view
  const canOpenDrawer = useMemo(() => {
    switch (activeView) {
      case 'fixed-interval':
      case 'wheelpath-profile':
        return visibleSegments.length > 0;
      case 'short-continuous':
        return visibleALRs.length > 0;
      case 'rolling-straightedge':
        return visibleBumpsDips.length > 0;
      default:
        return false;
    }
  }, [activeView, visibleSegments, visibleALRs, visibleBumpsDips]);

  // Open data drawer with first item selected
  const openDataDrawer = useCallback(() => {
    let type, data;
    switch (activeView) {
      case 'fixed-interval':
      case 'wheelpath-profile':
        type = 'segment';
        data = visibleSegments[0];
        break;
      case 'short-continuous':
        type = 'alr';
        data = visibleALRs[0];
        break;
      case 'rolling-straightedge':
        // Default to bumps, or dips if no bumps
        if (visibleBumps.length > 0) {
          type = 'bump';
          data = visibleBumps[0];
        } else {
          type = 'dip';
          data = visibleDips[0];
        }
        break;
      default:
        return;
    }
    if (data) {
      setSelectedItem({ type, data });
    }
  }, [activeView, visibleSegments, visibleALRs, visibleBumps, visibleDips]);

  // Initialize deck.gl overlay
useEffect(() => {
  if (!map) return;
  
  // interleaved: false forces separate canvas overlay instead of shared WebGL context
  // This fixes COLOR_ATTACHMENTi_EXT INVALID_OPERATION errors with Multiple Render Targets
  const overlay = new GoogleMapsOverlay({ layers: [], interleaved: false, useDevicePixels: true });
  overlay.setMap(map);
  deckOverlayRef.current = overlay;

  return () => {
    if (deckOverlayRef.current) {
      deckOverlayRef.current.setMap(null);
      deckOverlayRef.current.finalize();
      deckOverlayRef.current = null;
    }
  };
}, [map]);

  // Auto-orient map based on run direction
  useEffect(() => {
    if (!map || !segments || segments.length === 0 || hasOrientedRef.current) return;

    const sortedSegments = [...segments].sort((a, b) => a.from_station_ft - b.from_station_ft);
    const firstSeg = sortedSegments[0];
    const lastSeg = sortedSegments[sortedSegments.length - 1];

    const firstGeom = parseGeometry(firstSeg.geom);
    const lastGeom = parseGeometry(lastSeg.geom);

    if (!firstGeom?.coordinates?.length || !lastGeom?.coordinates?.length) return;

    const lowStationCoord = firstGeom.coordinates[0];
    const highStationCoord = lastGeom.coordinates[lastGeom.coordinates.length - 1];

    let startCoord, endCoord;
    if (isReversed) {
      startCoord = highStationCoord;
      endCoord = lowStationCoord;
    } else {
      startCoord = lowStationCoord;
      endCoord = highStationCoord;
    }

    const startLatLng = new window.google.maps.LatLng(startCoord[1], startCoord[0]);
    const endLatLng = new window.google.maps.LatLng(endCoord[1], endCoord[0]);
    const bearing = window.google.maps.geometry.spherical.computeHeading(startLatLng, endLatLng);
    const targetHeading = bearing - 90;

    map.setHeading(targetHeading);
    
    if (bounds) {
      const googleBounds = new window.google.maps.LatLngBounds(
        new window.google.maps.LatLng(bounds.min_lat, bounds.min_lon),
        new window.google.maps.LatLng(bounds.max_lat, bounds.max_lon)
      );
      map.fitBounds(googleBounds, 50);
    }

    hasOrientedRef.current = true;
  }, [map, segments, bounds, parseGeometry, isReversed]);

  // Set initial map view
  useEffect(() => {
    if (bounds && bounds.min_lon && bounds.max_lon && bounds.min_lat && bounds.max_lat) {
      const centerLat = (bounds.min_lat + bounds.max_lat) / 2;
      const centerLng = (bounds.min_lon + bounds.max_lon) / 2;
      setMapCenter({ lat: centerLat, lng: centerLng });
      setMapZoom(15);
    }
  }, [bounds]);

  // Fit bounds when map loads
  useEffect(() => {
    if (map && bounds && bounds.min_lon && bounds.max_lon && bounds.min_lat && bounds.max_lat) {
      const googleBounds = new window.google.maps.LatLngBounds(
        new window.google.maps.LatLng(bounds.min_lat, bounds.min_lon),
        new window.google.maps.LatLng(bounds.max_lat, bounds.max_lon)
      );
      map.fitBounds(googleBounds);
    }
  }, [map, bounds]);

  // Pan to selected item
  useEffect(() => {
    if (!map || !selectedItem) return;
    
    let coords = null;
    
    if (selectedItem.type === 'segment') {
      const geometry = parseGeometry(selectedItem.data.geom);
      if (geometry?.coordinates?.length) {
        const c = geometry.coordinates;
        coords = [
          (c[0][0] + c[c.length - 1][0]) / 2,
          (c[0][1] + c[c.length - 1][1]) / 2
        ];
      }
    } else if (selectedItem.type === 'alr' || selectedItem.type === 'bump' || selectedItem.type === 'dip') {
      const geometry = parseGeometry(selectedItem.data.geom);
      if (geometry?.coordinates?.length) {
        const c = geometry.coordinates;
        coords = [
          (c[0][0] + c[c.length - 1][0]) / 2,
          (c[0][1] + c[c.length - 1][1]) / 2
        ];
      }
    }
    
    if (coords) {
      map.panTo({ lat: coords[1], lng: coords[0] });
    }
  }, [selectedItem, map, parseGeometry]);

  // ========================================
  // DECK.GL LAYERS
  // ========================================

  // Start/End marker layers (always visible)
  const runMarkerLayers = useMemo(() => {
    if (!kmlMarkers?.start && !kmlMarkers?.end) return [];
    
    const layers = [];
    const getSpireScale = (zoom) => {
      if (zoom >= 20) return 0.5;
      if (zoom >= 18) return 1.5;
      if (zoom >= 16) return 3;
      if (zoom >= 14) return 6;
      return 15;
    };
    
    const scale = getSpireScale(currentZoom);
    const totalHeight = 50 * scale;
    const radius = 3 * scale;

    if (kmlMarkers?.start) {
      const { lng, lat } = kmlMarkers.start;
      
      layers.push(new ColumnLayer({
        id: 'run-start-col',
        data: [{ position: [lng, lat, 0] }],
        getPosition: d => d.position,
        getElevation: totalHeight,
        getFillColor: [0, 255, 0, 220],
        radius: radius,
        radiusUnits: 'meters',
        extruded: true,
        pickable: false
      }));

      const startFlag = 'data:image/svg+xml;base64,' + btoa('<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg"><path d="M20 10 L20 90 M20 10 L80 30 L80 50 L20 30 Z" fill="#00ff00" stroke="white" stroke-width="4"/></svg>');

      layers.push(new IconLayer({
        id: 'run-start-flag',
        data: [{ position: [lng, lat, totalHeight] }],
        getPosition: d => d.position,
        getIcon: () => ({ url: startFlag, width: 100, height: 100, anchorX: 20, anchorY: 50, mask: false }),
        getSize: 15 * scale,
        sizeUnits: 'meters',
        billboard: true,
        pickable: false,
        parameters: { depthTest: false }
      }));
    }

    if (kmlMarkers?.end) {
      const { lng, lat } = kmlMarkers.end;
      const numStripes = 6;
      const stripeHeight = totalHeight / numStripes;
      
      for (let i = 0; i < numStripes; i++) {
        const isRed = i % 2 === 0;
        layers.push(new ColumnLayer({
          id: `run-end-col-${i}`,
          data: [{ position: [lng, lat, i * stripeHeight] }],
          getPosition: d => d.position,
          getElevation: stripeHeight,
          getFillColor: isRed ? [255, 0, 0, 220] : [255, 255, 255, 220],
          radius: radius,
          radiusUnits: 'meters',
          extruded: true,
          pickable: false
        }));
      }

      const endFlag = 'data:image/svg+xml;base64,' + btoa('<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg"><rect x="20" y="10" width="60" height="40" fill="black"/><rect x="20" y="10" width="15" height="10" fill="white"/><rect x="50" y="10" width="15" height="10" fill="white"/><rect x="35" y="20" width="15" height="10" fill="white"/><rect x="65" y="20" width="15" height="10" fill="white"/><rect x="20" y="30" width="15" height="10" fill="white"/><rect x="50" y="30" width="15" height="10" fill="white"/><rect x="35" y="40" width="15" height="10" fill="white"/><rect x="65" y="40" width="15" height="10" fill="white"/><path d="M20 10 L20 90" stroke="white" stroke-width="4"/></svg>');

      layers.push(new IconLayer({
        id: 'run-end-flag',
        data: [{ position: [lng, lat, totalHeight] }],
        getPosition: d => d.position,
        getIcon: () => ({ url: endFlag, width: 100, height: 100, anchorX: 20, anchorY: 30, mask: false }),
        getSize: 15 * scale,
        sizeUnits: 'meters',
        billboard: true,
        pickable: false,
        parameters: { depthTest: false }
      }));
    }
    
    return layers;
  }, [kmlMarkers, currentZoom]);

  // Segment layers (Fixed Interval view) - matches FusionMap rendering
  const segmentLayers = useMemo(() => {
    if (activeView !== 'fixed-interval') return [];
    if (!visibleSegments.length) return [];

    const ELEVATION_METERS = 5;
    const CURTAIN_SKIP = 2;
    const CURTAIN_WIDTH_PX = 2;

    const data = visibleSegments.map(seg => {
      const geometry = parseGeometry(seg.geom);
      const coords = geometry?.coordinates || [];
      if (!coords.length) return null;

      const isSelected = selectedItem?.type === 'segment' && selectedItem?.data?.segment_id === seg.segment_id;
      const elevatedZ = isSelected ? ELEVATION_METERS : 0;

      const pathElevated = coords.map(([lng, lat]) => [lng, lat, elevatedZ]);
      const pathGround = coords.map(([lng, lat]) => [lng, lat, 0]);

      const curtainSegments = isSelected
        ? pathElevated
            .map((p, i) => (i % CURTAIN_SKIP === 0 || i === pathElevated.length - 1)
              ? { s: p, t: [p[0], p[1], 0] }
              : null
            )
            .filter(Boolean)
        : [];

      return {
        ...seg,
        __isSelected: isSelected,
        __pathElevated: pathElevated,
        __pathGround: pathGround,
        __curtain: curtainSegments
      };
    }).filter(Boolean);

    if (!data.length) return [];

    const nonSelectedData = data.filter(d => !d.__isSelected);
    const selectedData = data.filter(d => d.__isSelected);

    const layers = [];

    // Ground traces for all
    if (nonSelectedData.length > 0) {
      layers.push(new PathLayer({
        id: 'segment-ground-trace',
        data: nonSelectedData,
        getPath: d => d.__pathGround,
        getColor: [0, 0, 0, 60],
        getWidth: 1.5,
        widthUnits: 'pixels',
        capRounded: true,
        jointRounded: true,
        billboard: true,
        pickable: false,
        parameters: { depthTest: false }
      }));
    }

    // Non-selected segments - casing + color per segment
    nonSelectedData.forEach((segment) => {
      layers.push(new PathLayer({
        id: `segment-casing-${segment.segment_id}`,
        data: [segment],
        getPath: d => d.__pathElevated,
        getColor: [0, 0, 0, 255],
        getWidth: 6,
        widthUnits: 'pixels',
        capRounded: true,
        jointRounded: true,
        billboard: true,
        pickable: false,
        parameters: { depthTest: false }
      }));

      layers.push(new PathLayer({
        id: `segment-colored-${segment.segment_id}`,
        data: [segment],
        getPath: d => d.__pathElevated,
        getColor: d => {
          const color = getSegmentColor(d);
          const r = parseInt(color.slice(1, 3), 16);
          const g = parseInt(color.slice(3, 5), 16);
          const b = parseInt(color.slice(5, 7), 16);
          return [r, g, b, 255];
        },
        getWidth: 4,
        widthUnits: 'pixels',
        capRounded: true,
        jointRounded: true,
        billboard: true,
        pickable: true,
        autoHighlight: true,
        highlightColor: [255, 255, 255, 140],
        onClick: ({ object }) => {
          if (object) {
            setSelectedItem({ type: 'segment', data: object });
            setHoverTooltip(null);
          }
        },
        onHover: ({ object, x, y }) => {
          if (object) {
            const iri = object.iri_avg_in_per_mi || object.iri_in_per_mi || 0;
            setHoverTooltip({
              x, y,
              content: {
                type: 'Segment',
                iri: iri.toFixed(1),
                rating: getIRIRating(iri),
                station: `${formatStation(object.from_station_ft)} → ${formatStation(object.to_station_ft)}`
              }
            });
          } else {
            setHoverTooltip(null);
          }
        },
        parameters: { depthTest: false }
      }));
    });

    // Selected segment - elevated with curtain
    if (selectedData.length > 0) {
      layers.push(new PathLayer({
        id: 'segment-ground-trace-selected',
        data: selectedData,
        getPath: d => d.__pathGround,
        getColor: [0, 0, 0, 255],
        opacity: 0.2,
        getWidth: 3,
        widthUnits: 'pixels',
        capRounded: true,
        jointRounded: true,
        billboard: true,
        pickable: false,
        parameters: { depthTest: false }
      }));

      layers.push(new LineLayer({
        id: 'segment-curtain',
        data: selectedData.flatMap(d => d.__curtain),
        getSourcePosition: d => d.s,
        getTargetPosition: d => d.t,
        getColor: [0, 0, 0, 255],
        getWidth: CURTAIN_WIDTH_PX,
        widthUnits: 'pixels',
        pickable: false,
        billboard: true,
        parameters: { depthTest: false }
      }));

      layers.push(new PathLayer({
        id: 'segment-casing-selected',
        data: selectedData,
        getPath: d => d.__pathElevated,
        getColor: [255, 255, 255, 255],
        getWidth: 9,
        widthUnits: 'pixels',
        capRounded: true,
        jointRounded: true,
        billboard: true,
        pickable: false,
        parameters: { depthTest: false }
      }));

      layers.push(new PathLayer({
        id: 'segment-colored-selected',
        data: selectedData,
        getPath: d => d.__pathElevated,
        getColor: d => {
          const color = getSegmentColor(d);
          const r = parseInt(color.slice(1, 3), 16);
          const g = parseInt(color.slice(3, 5), 16);
          const b = parseInt(color.slice(5, 7), 16);
          return [r, g, b, 255];
        },
        getWidth: 8,
        widthUnits: 'pixels',
        capRounded: true,
        jointRounded: true,
        billboard: true,
        pickable: true,
        autoHighlight: true,
        highlightColor: [255, 255, 255, 140],
        onClick: ({ object }) => {
          if (object) {
            setSelectedItem({ type: 'segment', data: object });
            setHoverTooltip(null);
          }
        },
        parameters: { depthTest: false }
      }));
    }

    return layers;
  }, [visibleSegments, activeView, parseGeometry, getSegmentColor, selectedItem, getIRIRating, formatStation]);

  // IRI Text labels for Fixed Interval view
  const iriTextLayers = useMemo(() => {
    if (activeView !== 'fixed-interval' || currentZoom < IRI_TEXT_ZOOM_THRESHOLD) return [];
    if (!visibleSegments.length) return [];

    const textData = visibleSegments.map(segment => {
      const geometry = parseGeometry(segment.geom);
      if (!geometry || geometry.coordinates.length < 2) return null;

      const startCoord = geometry.coordinates[0];
      const endCoord = geometry.coordinates[geometry.coordinates.length - 1];
      const midpoint = [
        (startCoord[0] + endCoord[0]) / 2,
        (startCoord[1] + endCoord[1]) / 2,
        0
      ];

      const iriValue = segment.iri_avg_in_per_mi || segment.iri_in_per_mi || 0;

      return {
        position: midpoint,
        text: iriValue.toFixed(1),
        size: 13
      };
    }).filter(Boolean);

    return [
      new TextLayer({
        id: 'iri-text-labels',
        data: textData,
        getPosition: d => d.position,
        getText: d => d.text,
        getColor: [0, 0, 0, 255],
        getSize: d => d.size,
        getTextAnchor: 'middle',
        getAlignmentBaseline: 'center',
        getPixelOffset: [0, -25],
        background: true,
        getBackgroundColor: [255, 255, 255, 255],
        billboard: true,
        sizeMinPixels: 12,
        sizeMaxPixels: 24,
        backgroundPadding: [6, 6],
        backgroundBorderRadius: 99,
        getBorderColor: [0, 0, 0, 255],
        getBorderWidth: 1,
      })
    ];
  }, [activeView, visibleSegments, currentZoom, parseGeometry]);

  // Wheelpath Profile layers - IRI columns for full profile or selected segment
  const wheelpathProfileLayers = useMemo(() => {
    if (activeView !== 'wheelpath-profile' || !iriProfileData) return [];

    const layers = [];
    const Z_SCALE = 0.5;
    const COLUMN_RADIUS = 0.4;
    const COLUMN_INTERVAL = 2;

    // Get selected segment's station range
    const selectedFromStation = selectedItem?.type === 'segment' ? selectedItem.data.from_station_ft : null;
    const selectedToStation = selectedItem?.type === 'segment' ? selectedItem.data.to_station_ft : null;
    const hasSelection = selectedFromStation !== null && selectedToStation !== null;

    // Helper to get color based on IRI value
    const getIRIColorRGBA = (iri) => {
      let color;
      for (const range of IRI_COLOR_SCALE.ranges) {
        if (iri <= range.max) {
          const hex = range.color;
          const r = parseInt(hex.slice(1, 3), 16);
          const g = parseInt(hex.slice(3, 5), 16);
          const b = parseInt(hex.slice(5, 7), 16);
          color = [r, g, b];
          break;
        }
      }
      if (!color) color = [160, 160, 160];
      return [...color, 230];
    };

    // Find the segment that contains a given station
    const findSegmentForStation = (station) => {
      return visibleSegments.find(seg => 
        station >= seg.from_station_ft && station <= seg.to_station_ft
      );
    };

    // Generate column data - filter to selected segment if one is selected
    const generateColumnData = (points, wheelpathName) => {
      if (!points || points.length === 0) return [];
      
      if (wheelpathName === 'Left' && !wheelpathFilter.LEFT) return [];
      if (wheelpathName === 'Right' && !wheelpathFilter.RIGHT) return [];

      return points
        .filter((_, i) => i % COLUMN_INTERVAL === 0)
        .filter(p => p.iri !== null && p.iri !== undefined && p.iri > 0)
        .filter(p => {
          // If a segment is selected, only show columns within that segment's range
          if (hasSelection) {
            return p.station >= selectedFromStation && p.station <= selectedToStation;
          }
          return true; // Show all if no selection
        })
        .map(p => {
          const parentSegment = findSegmentForStation(p.station);

          return {
            position: [p.lng, p.lat, 0],
            height: Math.max(p.iri * Z_SCALE, 0.5),
            color: getIRIColorRGBA(p.iri),
            iri: p.iri,
            station: p.station,
            stationLabel: p.stationLabel,
            wheelpath: wheelpathName,
            parentSegment: parentSegment
          };
        });
    };

    const leftColumns = generateColumnData(iriProfileData.leftWheelpath, 'Left');
    const rightColumns = generateColumnData(iriProfileData.rightWheelpath, 'Right');
    const allColumns = [...leftColumns, ...rightColumns];

    if (allColumns.length > 0) {
      // Ground shadow dots
      layers.push(new ScatterplotLayer({
        id: 'wheelpath-profile-ground',
        data: allColumns,
        getPosition: d => d.position,
        getFillColor: [0, 0, 0, 30],
        getRadius: COLUMN_RADIUS * 1.0,
        radiusUnits: 'meters',
        pickable: false,
        parameters: { depthTest: false }
      }));

      // 3D columns
      layers.push(new ColumnLayer({
        id: 'wheelpath-profile-columns',
        data: allColumns,
        getPosition: d => d.position,
        getElevation: d => d.height,
        getFillColor: d => d.color,
        radius: COLUMN_RADIUS,
        radiusUnits: 'meters',
        diskResolution: 12,
        extruded: true,
        pickable: true,
        autoHighlight: true,
        highlightColor: [255, 255, 255, 150],
        material: {
          ambient: 0.6,
          diffuse: 0.5,
          shininess: 32,
          specularColor: [200, 200, 200]
        },
        onClick: ({ object }) => {
          if (object?.parentSegment) {
            setSelectedItem({ type: 'segment', data: object.parentSegment });
            setHoverTooltip(null);
          }
        },
        onHover: ({ object, x, y }) => {
          if (object) {
            setHoverTooltip({
              x, y,
              content: {
                type: 'Wheelpath Profile',
                iri: object.iri.toFixed(1),
                station: object.stationLabel || formatStation(object.station),
                wheelpath: object.wheelpath,
              }
            });
          } else {
            setHoverTooltip(null);
          }
        },
        parameters: { depthTest: true }
      }));
    }

    return layers;
  }, [activeView, iriProfileData, wheelpathFilter, IRI_COLOR_SCALE, formatStation, selectedItem, visibleSegments]);

  // Short Continuous Defects (ALRs) - matches FusionMap alrLayers
  const shortContinuousLayers = useMemo(() => {
    if (activeView !== 'short-continuous') return [];
    if (!visibleALRs.length) return [];

    const ELEVATION_METERS = 3;
    const CURTAIN_SKIP = 2;
    const CURTAIN_WIDTH_PX = 2;

    const data = visibleALRs.map(ev => {
      const geometry = parseGeometry(ev.geom);
      const coords = geometry?.coordinates || [];
      if (!coords.length) return null;

      const isSelected = selectedItem?.type === 'alr' && selectedItem?.data?.event_id === ev.event_id;
      const elevatedZ = isSelected ? ELEVATION_METERS : 0;

      const pathElevated = coords.map(([lng, lat]) => [lng, lat, elevatedZ]);
      const pathGround = coords.map(([lng, lat]) => [lng, lat, 0]);

      const curtainSegments = isSelected
        ? pathElevated
            .map((p, i) => (i % CURTAIN_SKIP === 0 || i === pathElevated.length - 1)
              ? { s: p, t: [p[0], p[1], 0] }
              : null
            )
            .filter(Boolean)
        : [];

      return {
        ...ev,
        __isSelected: isSelected,
        __pathElevated: pathElevated,
        __pathGround: pathGround,
        __curtain: curtainSegments
      };
    }).filter(Boolean);

    if (!data.length) return [];

    const nonSelectedData = data.filter(d => !d.__isSelected);
    const selectedData = data.filter(d => d.__isSelected);

    const opaque = (rgba) => {
      const [r = 0, g = 0, b = 0] = rgba || [0, 0, 0];
      return [r, g, b, 255];
    };

    const layers = [];

    // All ALRs at ground level
    if (data.length > 0) {
      layers.push(new PathLayer({
        id: 'alr-ground-trace',
        data: data,
        getPath: d => d.__pathGround,
        getColor: [0, 0, 0, 60],
        getWidth: 2,
        widthUnits: 'pixels',
        capRounded: true,
        jointRounded: true,
        billboard: true,
        pickable: false,
        parameters: { depthTest: false }
      }));

      layers.push(new PathLayer({
        id: 'alr-casing-all',
        data: data,
        getPath: d => d.__pathGround,
        getColor: [0, 0, 0, 255],
        getWidth: 4.5,
        widthUnits: 'pixels',
        capRounded: true,
        jointRounded: true,
        billboard: true,
        pickable: false,
        parameters: { depthTest: false }
      }));

      layers.push(new PathLayer({
        id: 'alr-colored-all',
        data: data,
        getPath: d => d.__pathGround,
        getColor: d => opaque(getALRColor(d.magnitude_in ?? 0)),
        getWidth: 2.5,
        widthUnits: 'pixels',
        capRounded: true,
        jointRounded: true,
        billboard: true,
        pickable: true,
        autoHighlight: true,
        highlightColor: [255, 255, 255, 140],
        onClick: ({ object }) => {
          if (object) {
            setSelectedItem({ type: 'alr', data: object });
            setHoverTooltip(null);
          }
        },
        onHover: ({ object, x, y }) => {
          if (object) {
            setHoverTooltip({
              x, y,
              content: {
                type: 'Short Continuous Defect (ALR)',
                magnitude: (object.magnitude_in || 0).toFixed(1),
                station: `${formatStation(object.from_station_ft)} → ${formatStation(object.to_station_ft)}`,
                wheelpath: object.wheelpath || '—'
              }
            });
          } else {
            setHoverTooltip(null);
          }
        },
        parameters: { depthTest: false }
      }));
    }

    // Selected ALR - elevated
    if (selectedData.length > 0) {
      layers.push(new PathLayer({
        id: 'alr-ground-selected',
        data: selectedData,
        getPath: d => d.__pathGround,
        getColor: [0, 0, 0, 255],
        opacity: 0.2,
        getWidth: 3,
        widthUnits: 'pixels',
        capRounded: true,
        jointRounded: true,
        billboard: true,
        pickable: false,
        parameters: { depthTest: false }
      }));

      layers.push(new LineLayer({
        id: 'alr-curtain',
        data: selectedData.flatMap(d => d.__curtain),
        getSourcePosition: d => d.s,
        getTargetPosition: d => d.t,
        getColor: [0, 0, 0, 255],
        getWidth: CURTAIN_WIDTH_PX,
        widthUnits: 'pixels',
        pickable: false,
        billboard: true,
        parameters: { depthTest: false }
      }));

      layers.push(new PathLayer({
        id: 'alr-casing-selected',
        data: selectedData,
        getPath: d => d.__pathElevated,
        getColor: [255, 255, 255, 255],
        getWidth: 6.5,
        widthUnits: 'pixels',
        capRounded: true,
        jointRounded: true,
        billboard: true,
        pickable: false,
        parameters: { depthTest: false }
      }));

      layers.push(new PathLayer({
        id: 'alr-colored-selected',
        data: selectedData,
        getPath: d => d.__pathElevated,
        getColor: d => opaque(getALRColor(d.magnitude_in ?? 0)),
        getWidth: 4,
        widthUnits: 'pixels',
        capRounded: true,
        jointRounded: true,
        billboard: true,
        pickable: true,
        autoHighlight: true,
        highlightColor: [255, 255, 255, 140],
        onClick: ({ object }) => {
          if (object) {
            setSelectedItem({ type: 'alr', data: object });
            setHoverTooltip(null);
          }
        },
        parameters: { depthTest: false }
      }));
    }

    return layers;
  }, [activeView, visibleALRs, parseGeometry, selectedItem, formatStation]);

  // Rolling Straightedge (Bumps & Dips) - matches FusionMap pyramid style
  const rollingStraitedgeLayers = useMemo(() => {
    if (activeView !== 'rolling-straightedge') return [];
    if (!visibleBumpsDips.length) return [];

    const ELEVATION_SCALE = 10;
    const NUM_LEVELS = 10;
    const NUM_SEGMENTS = 48;

    const interpolatePosition = (geometry, fromStation, toStation, currentStation) => {
      const coords = geometry.coordinates;
      if (coords.length < 2) return coords[0];
      const ratio = (currentStation - fromStation) / (toStation - fromStation);
      const clampedRatio = Math.max(0, Math.min(1, ratio));
      const startCoord = coords[0];
      const endCoord = coords[coords.length - 1];
      return [
        startCoord[0] + (endCoord[0] - startCoord[0]) * clampedRatio,
        startCoord[1] + (endCoord[1] - startCoord[1]) * clampedRatio
      ];
    };

    const segmentData = [];
    visibleBumpsDips.forEach(event => {
      const fromStation = event.from_station_ft;
      const toStation = event.to_station_ft;
      const peakStation = event.peak_station_ft || fromStation;
      const magnitude = event.magnitude_in || 0;
      const geometry = parseGeometry(event.geom);
      const isBump = (event.event_type || '').toUpperCase() === 'BUMP';
      
      if (!geometry) return;
      
      const isSelected = 
        (selectedItem?.type === 'bump' || selectedItem?.type === 'dip') && 
        selectedItem?.data?.event_id === event.event_id;
      
      for (let i = 0; i < NUM_SEGMENTS; i++) {
        const segmentRatio = i / (NUM_SEGMENTS - 1);
        const currentStation = fromStation + (toStation - fromStation) * segmentRatio;
        
        const distanceToPeak = Math.abs(currentStation - peakStation);
        const maxDistFromPeak = Math.max(peakStation - fromStation, toStation - peakStation);
        const heightRatio = maxDistFromPeak > 0 
          ? 1 - Math.pow(distanceToPeak / maxDistFromPeak, 2)
          : 1;
        
        const segmentHeight = magnitude * Math.max(0, heightRatio);
        const position = interpolatePosition(geometry, fromStation, toStation, currentStation);
        
        if (position) {
          segmentData.push({
            ...event,
            segmentIndex: i,
            position,
            segmentHeight,
            currentStation,
            isBump,
            __isSelected: isSelected
          });
        }
      }
    });

    const layers = [];

    for (let level = 0; level < NUM_LEVELS; level++) {
      const levelRatio = (NUM_LEVELS - level) / NUM_LEVELS;
      
      layers.push(new ColumnLayer({
        id: `bump-dip-level-${level}`,
        data: segmentData,
        getPosition: d => {
          const [lng, lat] = d.position;
          const levelHeight = (d.segmentHeight * ELEVATION_SCALE * level) / NUM_LEVELS;
          return [lng, lat, levelHeight];
        },
        getElevation: d => (d.segmentHeight * ELEVATION_SCALE) / NUM_LEVELS,
        getFillColor: d => {
          const baseAlpha = 255 - (level * 12);
          const highlightBoost = d.__isSelected ? 40 : 0;
          return d.isBump 
            ? [255, 0, 0, Math.min(255, baseAlpha + highlightBoost)]
            : [255, 140, 0, Math.min(255, baseAlpha + highlightBoost)];
        },
        flatShading: false,
        coverage: 1.4,
        diskResolution: 32,
        radius: 0.18 * levelRatio,
        radiusUnits: 'meters',
        extruded: true,
        material: {
          ambient: 0.7,
          diffuse: 0.3,
          shininess: 8,
          specularColor: [15, 15, 15]
        },
        pickable: level === 0,
        autoHighlight: level === 0,
        highlightColor: [255, 255, 255, 150],
        onClick: level === 0 ? ({ object }) => {
          if (object) {
            setSelectedItem({ 
              type: object.isBump ? 'bump' : 'dip', 
              data: object 
            });
            setHoverTooltip(null);
          }
        } : undefined,
        onHover: level === 0 ? ({ object, x, y }) => {
          if (object) {
            setHoverTooltip({
              x, y,
              content: {
                type: object.isBump ? 'Bump' : 'Dip',
                magnitude: (object.magnitude_in || 0).toFixed(2),
                station: `${formatStation(object.from_station_ft)} → ${formatStation(object.to_station_ft)}`,
                wheelpath: object.wheelpath || '—'
              }
            });
          } else {
            setHoverTooltip(null);
          }
        } : undefined,
        parameters: { depthTest: false }
      }));
    }

    return layers;
  }, [activeView, visibleBumpsDips, parseGeometry, selectedItem, formatStation]);

  // Update deck.gl layers
  useEffect(() => {
    if (!deckOverlayRef.current) return;
    
    const layers = [
      ...runMarkerLayers,
      ...segmentLayers,
      ...iriTextLayers,
      ...wheelpathProfileLayers,
      ...shortContinuousLayers,
      ...rollingStraitedgeLayers
    ];
    
    deckOverlayRef.current.setProps({ layers });
  }, [runMarkerLayers, segmentLayers, iriTextLayers, wheelpathProfileLayers, shortContinuousLayers, rollingStraitedgeLayers]);

  // Map callbacks
  const onLoad = useCallback((map) => {
    setMap(map);
    mapRef.current = map;
    setCurrentZoom(map.getZoom());
    map.setTilt(60);
    map.setHeading(315);
  }, []);

  const onUnmount = useCallback(() => {
    setMap(null);
    mapRef.current = null;
  }, []);

  const handleZoomChanged = useCallback(() => {
    if (mapRef.current) {
      setCurrentZoom(mapRef.current.getZoom());
    }
  }, []);

  const handleMapClick = useCallback(() => {
    setSelectedItem(null);
  }, []);

  const mapOptions = useMemo(() => ({
    disableDefaultUI: true,
    clickableIcons: false,
    mapTypeId: mapStyleMode === 'satellite' ? 'satellite' : 'roadmap',
    mapId: '24ee09cb1783e3c39c8fcb02',
    minZoom: 10,
    gestureHandling: 'greedy',
    rotateControl: false,
    scaleControl: false,
    streetViewControl: false,
    fullscreenControl: false,
    mapTypeControl: false,
    zoomControl: false,
  }), [mapStyleMode]);

  // Map control functions
  const zoomIn = useCallback(() => mapRef.current && mapRef.current.setZoom(Math.min(mapRef.current.getZoom() + 1, 22)), []);
  const zoomOut = useCallback(() => mapRef.current && mapRef.current.setZoom(Math.max(mapRef.current.getZoom() - 1, 10)), []);
  const adjustMap = useCallback((mode, amount) => {
    if (!mapRef.current) return;
    switch (mode) {
      case "tilt":
        mapRef.current.setTilt(Math.max(0, Math.min(67.5, mapRef.current.getTilt() + amount)));
        break;
      case "rotate":
        mapRef.current.setHeading((mapRef.current.getHeading() + amount + 360) % 360);
        break;
      default:
        break;
    }
  }, []);

  if (loadError) return <div className="h-full flex items-center justify-center text-red-600">Error Loading Maps</div>;
  if (!isLoaded) return <div className="h-full flex items-center justify-center text-gray-600">Loading...</div>;

  const activeViewConfig = VIEW_MODES.find(v => v.id === activeView);

  return (
    <div className="relative w-full h-full flex flex-col bg-gray-100">
      {/* Header Bar */}
      <div className="flex-shrink-0 h-12 bg-white border-b border-gray-200">
        <div className="h-full px-2 sm:px-4 flex items-center justify-between gap-2">
        {/* Left: View Dropdown (hidden on mobile) + Table Button */}
        <div className="flex items-center gap-1.5 sm:gap-2 min-w-0">
          {/* View Dropdown - Hidden on mobile, shown in bottom left instead */}
          <div className="relative min-w-0 hidden sm:block" ref={viewDropdownRef}>
            <button
              onClick={() => setViewDropdownOpen(!viewDropdownOpen)}
              className="flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-sm text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors"
            >
              <span className="truncate text-xs sm:text-sm">{activeViewConfig?.label || 'Select View'}</span>
              <ChevronDownIcon className={`h-4 w-4 flex-shrink-0 transition-transform ${viewDropdownOpen ? 'rotate-180' : ''}`} />
            </button>

            {viewDropdownOpen && (
              <div className="absolute top-full left-0 mt-1 w-64 sm:w-72 bg-white rounded-sm shadow-lg border border-gray-200 z-50 overflow-hidden">
                {VIEW_MODES.map((view) => (
                  <button
                    key={view.id}
                    onClick={() => {
                      setActiveView(view.id);
                      setViewDropdownOpen(false);
                      setSelectedItem(null);
                    }}
                    className={`w-full px-3 py-2.5 text-left hover:bg-gray-50 transition-colors ${
                      activeView === view.id ? 'bg-red-50 border-l-2 border-red-600' : 'border-l-2 border-transparent'
                    }`}
                  >
                    <div className={`text-sm font-medium ${activeView === view.id ? 'text-red-700' : 'text-gray-900'}`}>
                      {view.label}
                    </div>
                    <div className="text-xs text-gray-500">{view.description}</div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Table Button */}
          <button
            onClick={() => openDataDrawer()}
            disabled={!canOpenDrawer}
            title="View Data Table"
            className={`p-2 border border-gray-200 rounded-sm transition-colors flex-shrink-0 ${
              selectedItem 
                ? 'bg-red-600 text-white border-red-600' 
                : canOpenDrawer
                  ? 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                  : 'bg-gray-50 text-gray-300 cursor-not-allowed'
            }`}
          >
            <TableCellsIcon className="h-4 w-4" />
          </button>
        </div>

        {/* Right: Controls */}
        <div className="flex items-center gap-1.5 sm:gap-2">
          {/* Wheelpath Toggle */}
          <div className="flex rounded-sm overflow-hidden border border-gray-200">
            <button
              onClick={() => setWheelpathFilter(prev => ({ ...prev, LEFT: !prev.LEFT }))}
              className={`px-2 sm:px-2.5 py-1.5 text-sm sm:text-sm font-semibold transition-colors ${
                wheelpathFilter.LEFT 
                  ? 'bg-red-600 text-white' 
                  : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              L
            </button>
            <div className="w-px bg-gray-200" />
            <button
              onClick={() => setWheelpathFilter(prev => ({ ...prev, RIGHT: !prev.RIGHT }))}
              className={`px-2 sm:px-2.5 py-1.5 text-sm sm:text-sm font-semibold transition-colors ${
                wheelpathFilter.RIGHT 
                  ? 'bg-red-600 text-white' 
                  : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              R
            </button>
          </div>

          <div className="w-px h-6 bg-gray-200 hidden sm:block" />

          {/* Map Style */}
          <div className="flex rounded-sm overflow-hidden border border-gray-200">
            <ControlButton
              onClick={() => setMapStyleMode('satellite')}
              isActive={mapStyleMode === 'satellite'}
              title="Satellite"
            >
              <SatelliteIcon className="h-4 w-4" />
            </ControlButton>
            <div className="w-px bg-gray-200" />
            <ControlButton
              onClick={() => setMapStyleMode('roadmap')}
              isActive={mapStyleMode === 'roadmap'}
              title="Roadmap"
            >
              <MapIcon className="h-4 w-4" />
            </ControlButton>
          </div>

          {/* Rotate/Tilt Controls */}
          <div className="flex items-center gap-1 sm:gap-2">
            <div className="w-px h-6 bg-gray-200 hidden sm:block" />
            <div className="flex rounded-sm overflow-hidden border border-gray-200">
              <ControlButton onClick={() => adjustMap("rotate", 45)} title="Rotate Left">
                <RotateCcwSquare className="h-4 w-4" />
              </ControlButton>
              <div className="w-px bg-gray-200" />
              <ControlButton onClick={() => adjustMap("tilt", -15)} title="Tilt Up">
                <ArrowUpFromLine className="h-4 w-4" />
              </ControlButton>
              <div className="w-px bg-gray-200" />
              <ControlButton onClick={() => adjustMap("tilt", 15)} title="Tilt Down">
                <ArrowDownFromLine className="h-4 w-4" />
              </ControlButton>
              <div className="w-px bg-gray-200" />
              <ControlButton onClick={() => adjustMap("rotate", -45)} title="Rotate Right">
                <RotateCwSquare className="h-4 w-4" />
              </ControlButton>
            </div>
          </div>

          {/* Zoom Controls - Hidden on mobile (pinch to zoom) */}
          <div className="hidden md:flex items-center gap-2">
            <div className="w-px h-6 bg-gray-200" />
            <div className="flex rounded-sm overflow-hidden border border-gray-200">
              <ControlButton onClick={zoomIn} title="Zoom In">
                <Plus className="h-4 w-4" />
              </ControlButton>
              <div className="w-px bg-gray-200" />
              <ControlButton onClick={zoomOut} title="Zoom Out">
                <Minus className="h-4 w-4" />
              </ControlButton>
            </div>
          </div>
        </div>
        </div>
      </div>

      {/* Map Container */}
      <div className="relative flex-1">
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          center={mapCenter}
          zoom={mapZoom}
          onLoad={onLoad}
          onUnmount={onUnmount}
          onZoomChanged={handleZoomChanged}
          onClick={handleMapClick}
          options={mapOptions}
        />

        {/* Mobile: View Dropdown (bottom left, adjusts when drawer is open) */}
        <div 
          className={`absolute left-2 sm:hidden z-10 transition-all duration-300 ${
            selectedItem ? 'bottom-2' : 'bottom-24'
          }`}
          ref={mobileViewDropdownRef}
        >
          <button
            onClick={() => setViewDropdownOpen(!viewDropdownOpen)}
            className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 rounded-sm shadow-md text-sm font-medium text-gray-700"
          >
            <span className="truncate">{activeViewConfig?.label || 'Select'}</span>
            <ChevronDownIcon className={`h-4 w-4 flex-shrink-0 transition-transform ${viewDropdownOpen ? 'rotate-180' : ''}`} />
          </button>

          {viewDropdownOpen && (
            <div className="absolute bottom-full left-0 mb-1 w-64 bg-white rounded-sm shadow-lg border border-gray-200 z-50 overflow-hidden">
              {VIEW_MODES.map((view) => (
                <button
                  key={view.id}
                  onClick={() => {
                    setActiveView(view.id);
                    setViewDropdownOpen(false);
                    setSelectedItem(null);
                  }}
                  className={`w-full px-3 py-2.5 text-left hover:bg-gray-50 transition-colors ${
                    activeView === view.id ? 'bg-red-50 border-l-2 border-red-600' : 'border-l-2 border-transparent'
                  }`}
                >
                  <div className={`text-sm font-medium ${activeView === view.id ? 'text-red-700' : 'text-gray-900'}`}>
                    {view.label}
                  </div>
                  <div className="text-xs text-gray-500">{view.description}</div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Desktop: Legend (bottom left) */}
        <div className="absolute bottom-3 left-3 bg-white rounded-sm shadow-md z-10 max-w-xs hidden sm:block">
          <button 
            onClick={() => setLegendCollapsed(!legendCollapsed)}
            className="w-full px-3 py-2 flex items-center justify-between text-left"
          >
            <h4 className="text-xs font-semibold text-gray-700 truncate">{activeViewConfig?.label}</h4>
            <ChevronDownIcon className={`h-3 w-3 text-gray-400 flex-shrink-0 ml-2 transition-transform ${legendCollapsed ? '' : 'rotate-180'}`} />
          </button>
          
          <div className={`overflow-hidden transition-all duration-200 ${legendCollapsed ? 'max-h-0' : 'max-h-64'}`}>
            <div className="px-3 pb-3 space-y-1">
              {activeView === 'fixed-interval' && (
                <>
                  {IRI_COLOR_SCALE.ranges.map((range, i) => {
                    const prevMax = i === 0 ? 0 : IRI_COLOR_SCALE.ranges[i - 1].max;
                    const isLast = i === IRI_COLOR_SCALE.ranges.length - 1;
                    const rangeText = isLast ? `> ${prevMax}` : `${prevMax}-${range.max}`;
                    return (
                      <div key={i} className="flex items-center gap-2">
                        <div className="w-3 h-2 rounded-sm flex-shrink-0" style={{ backgroundColor: range.color }} />
                        <span className="text-xs text-gray-600">{range.label} ({rangeText})</span>
                      </div>
                    );
                  })}
                </>
              )}

              {activeView === 'wheelpath-profile' && (
                <>
                  {IRI_COLOR_SCALE.ranges.map((range, i) => {
                    const prevMax = i === 0 ? 0 : IRI_COLOR_SCALE.ranges[i - 1].max;
                    const isLast = i === IRI_COLOR_SCALE.ranges.length - 1;
                    const rangeText = isLast ? `> ${prevMax}` : `${prevMax}-${range.max}`;
                    return (
                      <div key={i} className="flex items-center gap-2">
                        <div className="w-3 h-2 rounded-sm flex-shrink-0" style={{ backgroundColor: range.color }} />
                        <span className="text-xs text-gray-600">{range.label} ({rangeText})</span>
                      </div>
                    );
                  })}
                  <p className="mt-2 text-xs text-gray-500">
                    {selectedItem?.type === 'segment' 
                      ? 'Showing selected segment only' 
                      : 'Select a segment to filter'}
                  </p>
                </>
              )}

              {activeView === 'short-continuous' && (
                <>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-2 rounded-sm flex-shrink-0" style={{ backgroundColor: 'rgb(255, 193, 7)' }} />
                    <span className="text-xs text-gray-600">Low severity</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-2 rounded-sm flex-shrink-0" style={{ backgroundColor: 'rgb(255, 152, 0)' }} />
                    <span className="text-xs text-gray-600">Medium (&gt;200)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-2 rounded-sm flex-shrink-0" style={{ backgroundColor: 'rgb(244, 67, 54)' }} />
                    <span className="text-xs text-gray-600">High (&gt;250)</span>
                  </div>
                  <p className="mt-2 text-xs text-gray-500">Localized Roughness (ALR)</p>
                  <p className="text-xs text-gray-400">{visibleALRs.length} defect{visibleALRs.length !== 1 ? 's' : ''}</p>
                </>
              )}

              {activeView === 'rolling-straightedge' && (
                <>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-2 rounded-sm bg-red-500 flex-shrink-0" />
                    <span className="text-xs text-gray-600">Bumps ({visibleBumps.length})</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-2 rounded-sm bg-orange-500 flex-shrink-0" />
                    <span className="text-xs text-gray-600">Dips ({visibleDips.length})</span>
                  </div>
                  <p className="mt-2 text-xs text-gray-500">Height indicates magnitude</p>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Hover Tooltip */}
        {hoverTooltip && (
          <div
            className="fixed bg-gray-800 text-white p-2.5 rounded-sm shadow-lg text-xs z-50 pointer-events-none"
            style={{
              left: hoverTooltip.x + 10,
              top: hoverTooltip.y - 10,
              transform: 'translateY(-100%)'
            }}
          >
            <div className="font-semibold mb-1">{hoverTooltip.content.type}</div>
            {hoverTooltip.content.iri && <div>IRI: {hoverTooltip.content.iri} in/mi</div>}
            {hoverTooltip.content.rating && <div>Rating: {hoverTooltip.content.rating}</div>}
            {hoverTooltip.content.magnitude && <div>Magnitude: {hoverTooltip.content.magnitude} {hoverTooltip.content.type?.includes('Bump') || hoverTooltip.content.type?.includes('Dip') ? 'in' : 'in/mi'}</div>}
            {hoverTooltip.content.maxIri && <div>Max IRI: {hoverTooltip.content.maxIri} in/mi</div>}
            {hoverTooltip.content.avgIri && <div>Avg IRI: {hoverTooltip.content.avgIri} in/mi</div>}
            {hoverTooltip.content.station && <div>Station: {hoverTooltip.content.station}</div>}
            {hoverTooltip.content.defectRange && <div>Defect: {hoverTooltip.content.defectRange}</div>}
            {hoverTooltip.content.wheelpath && <div>Wheelpath: {hoverTooltip.content.wheelpath}</div>}
            {hoverTooltip.content.length && <div>Length: {hoverTooltip.content.length} ft</div>}
          </div>
        )}
      </div>

      {/* Bottom Drawer with Data Table */}
      <div 
        className={`flex-shrink-0 bg-white border-t border-gray-200 transition-all duration-300 ease-in-out overflow-hidden`}
        style={{ 
          height: selectedItem 
            ? 'calc(20rem + env(safe-area-inset-bottom, 0px))' // ~h-80 + safe area for mobile browser nav
            : '0'
        }}
      >
        {selectedItem && (
          <div className="h-full flex flex-col">
            {/* Drawer Header */}
            <div className="flex-shrink-0 px-2 sm:px-4 py-2 border-b border-gray-100 flex items-center justify-between bg-gray-50 gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <h3 className="text-xs sm:text-sm font-semibold text-gray-900 truncate">
                  {selectedItem.type === 'segment' && 'Segments'}
                  {selectedItem.type === 'alr' && 'ALRs'}
                  {selectedItem.type === 'bump' && 'Bumps'}
                  {selectedItem.type === 'dip' && 'Dips'}
                </h3>
                <span className="text-xs text-gray-400 bg-gray-200 px-1.5 sm:px-2 py-0.5 rounded-full flex-shrink-0">
                  {tableData.length}
                </span>
              </div>

              {/* Navigation & Close */}
              <div className="flex items-center gap-0.5 sm:gap-1 flex-shrink-0">
                <button
                  onClick={() => navigateItem('prev')}
                  disabled={currentItemIndex <= 0}
                  className="p-1.5 rounded-sm hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  title="Previous"
                >
                  <ChevronLeftIcon className="h-4 w-4 text-gray-600" />
                </button>
                <span className="text-xs text-gray-500 min-w-[3rem] sm:min-w-[4rem] text-center font-mono">
                  {currentItemIndex + 1}/{tableData.length}
                </span>
                <button
                  onClick={() => navigateItem('next')}
                  disabled={currentItemIndex >= tableData.length - 1}
                  className="p-1.5 rounded-sm hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  title="Next"
                >
                  <ChevronRightIcon className="h-4 w-4 text-gray-600" />
                </button>
                <div className="w-px h-4 bg-gray-300 mx-1 sm:mx-2" />
                <button
                  onClick={() => setSelectedItem(null)}
                  className="p-1.5 rounded-sm hover:bg-gray-200 transition-colors"
                  title="Close"
                >
                  <XMarkIcon className="h-4 w-4 text-gray-600" />
                </button>
              </div>
            </div>

            {/* Data Table */}
            <div className="flex-1 overflow-hidden">
              <DataTable
                columns={getTableColumns(selectedItem.type)}
                data={tableData}
                selectedId={selectedItem.data[getIdKey(selectedItem.type)]}
                onRowClick={handleRowClick}
                idKey={getIdKey(selectedItem.type)}
                formatStation={formatStation}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default SharedFusionMap;